/**
 * Data layer for `click-log-report`. Thin typed fetch wrappers over the
 * backing `/api/v1/click-level-detail-reports` endpoints (shipped backend).
 * Raw `fetch()` is confined to this file per the dash contract (rule D6).
 */

// ---------------------------------------------------------------------------
// Types — mirror the backend ClickDetailView shape.
// ---------------------------------------------------------------------------

export type ClickStatus =
	| "pending"
	| "accepted"
	| "rejected"
	| "duplicate"
	| "bot";

export const ALLOWED_STATUSES: readonly ClickStatus[] = [
	"pending",
	"accepted",
	"rejected",
	"duplicate",
	"bot",
] as const;

export interface ClickLogRow {
	click_id: string;
	ts: number;
	ip: string | null;
	ua: string | null;
	country: string | null;
	device_type: string | null;
	os: string | null;
	browser: string | null;
	campaign_id: string | null;
	affiliate_id: string | null;
	placement_id: string | null;
	creative_id: string | null;
	referrer: string | null;
	landing_url: string | null;
	attributed_conversion_id: string | null;
	status: ClickStatus;
	created_at: number;
	updated_at: number;
}

export interface ClickLogFilters {
	since?: number | null;
	until?: number | null;
	campaign_id?: string | null;
	affiliate_id?: string | null;
	country?: string | null;
	device_type?: string | null;
	status?: ClickStatus[] | null;
	click_id?: string | null; // client-side post-filter match (exact)
}

export interface ClickLogListResponse {
	data: ClickLogRow[];
	meta: {
		per_page: number;
		total: number;
		next_cursor: string | null;
	};
}

const BASE = "/api/v1/click-level-detail-reports";
const PER_PAGE_DEFAULT = 100;

// ---------------------------------------------------------------------------
// Envelope parser — `{error}` envelope unwraps to thrown Error.
// ---------------------------------------------------------------------------

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

// ---------------------------------------------------------------------------
// Query-string builder — keeps api.ts the sole place that knows how filters
// map to backend params.
// ---------------------------------------------------------------------------

export function buildClickLogQuery(
	filters: ClickLogFilters,
	cursor?: string | null,
	perPage: number = PER_PAGE_DEFAULT,
): string {
	const p = new URLSearchParams();
	if (filters.since != null) p.set("since", String(filters.since));
	if (filters.until != null) p.set("until", String(filters.until));
	if (filters.campaign_id) p.set("campaign_id", filters.campaign_id);
	if (filters.affiliate_id) p.set("affiliate_id", filters.affiliate_id);
	if (filters.country) p.set("country", filters.country);
	if (filters.device_type) p.set("device_type", filters.device_type);
	if (filters.status && filters.status.length > 0) {
		p.set("status", filters.status.join(","));
	}
	if (cursor) p.set("cursor", cursor);
	p.set("per_page", String(perPage));
	return p.toString();
}

// ---------------------------------------------------------------------------
// queryClicks — GET /clicks filtered + cursor-paginated.
// Click-ID exact match is applied client-side on the returned page (the
// backend indexes by click_id primary key; small fan-in cost acceptable for
// a forensic filter).
// ---------------------------------------------------------------------------

export async function queryClicks(args: {
	filters: ClickLogFilters;
	cursor?: string | null;
	perPage?: number;
}): Promise<ClickLogListResponse> {
	const qs = buildClickLogQuery(args.filters, args.cursor, args.perPage);
	const res = await fetch(`${BASE}/clicks${qs ? `?${qs}` : ""}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	const payload = await parseOrThrow<ClickLogListResponse>(res);
	// Client-side exact click_id filter — runs only on fetched page.
	if (args.filters.click_id && args.filters.click_id.trim()) {
		const q = args.filters.click_id.trim();
		return {
			...payload,
			data: payload.data.filter((r) => r.click_id === q),
		};
	}
	return payload;
}

// ---------------------------------------------------------------------------
// exportClicksCsv — POST /export-csv returns a text/csv blob stream.
// Caller receives a Blob ready to wire into `<a download>` for the browser
// download flow.
// ---------------------------------------------------------------------------

export async function exportClicksCsv(args: {
	filters: ClickLogFilters;
}): Promise<{ blob: Blob; rowCount: number | null; filename: string }> {
	const body: Record<string, unknown> = {};
	const f = args.filters;
	if (f.since != null) body.since = f.since;
	if (f.until != null) body.until = f.until;
	if (f.campaign_id) body.campaign_id = f.campaign_id;
	if (f.affiliate_id) body.affiliate_id = f.affiliate_id;
	if (f.country) body.country = f.country;
	if (f.device_type) body.device_type = f.device_type;
	if (f.status && f.status.length > 0) body.status = f.status;

	const res = await fetch(`${BASE}/export-csv`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "text/csv",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	if (!res.ok) {
		// Error bodies are JSON even on the CSV endpoint.
		const text = await res.text();
		let err: unknown = null;
		try {
			err = JSON.parse(text);
		} catch {
			err = text;
		}
		const msg =
			err &&
			typeof err === "object" &&
			err !== null &&
			"error" in err &&
			typeof (err as { error: unknown }).error === "string"
				? (err as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	const rowHeader = res.headers.get("x-row-count");
	const rowCount = rowHeader ? Number.parseInt(rowHeader, 10) : null;
	const blob = await res.blob();
	return {
		blob,
		rowCount: Number.isFinite(rowCount) ? rowCount : null,
		filename: "clicks.csv",
	};
}

// ---------------------------------------------------------------------------
// Total row-count preview — reuses GET /clicks with per_page=1 to cheaply
// compute the filtered total before an export decision.
// ---------------------------------------------------------------------------

export async function previewClickCount(args: {
	filters: ClickLogFilters;
}): Promise<number> {
	const qs = buildClickLogQuery(args.filters, null, 1);
	const res = await fetch(`${BASE}/clicks${qs ? `?${qs}` : ""}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	const payload = await parseOrThrow<ClickLogListResponse>(res);
	return Number(payload.meta.total ?? 0);
}

// ---------------------------------------------------------------------------
// Query keys — TanStack Query convention `['<slug>', ...]`.
// ---------------------------------------------------------------------------

export const clickLogReportQueryKeys = {
	all: ["click-log-report"] as const,
	list: (filters: ClickLogFilters) =>
		[...["click-log-report"], "list", filters] as const,
	count: (filters: ClickLogFilters) =>
		[...["click-log-report"], "count", filters] as const,
};

// ---------------------------------------------------------------------------
// Presentational helpers — pure functions, no DOM, safe to unit-test and
// reuse from the page component.
// ---------------------------------------------------------------------------

export function formatUtcTimestamp(tsSec: number | null | undefined): string {
	if (tsSec == null || !Number.isFinite(tsSec)) return "";
	const d = new Date(tsSec * 1000);
	const iso = d.toISOString(); // e.g. 2026-04-21T04:12:34.000Z
	return iso.replace("T", " ").replace(/\.\d+Z$/, "Z");
}

export function truncateMiddle(s: string | null | undefined, max = 32): string {
	if (!s) return "";
	if (s.length <= max) return s;
	const head = Math.ceil((max - 1) / 2);
	const tail = Math.floor((max - 1) / 2);
	return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

export function truncateEnd(s: string | null | undefined, max = 40): string {
	if (!s) return "";
	if (s.length <= max) return s;
	return `${s.slice(0, max - 1)}…`;
}

export function statusBadgeClass(status: ClickStatus): string {
	switch (status) {
		case "accepted":
			return "bg-emerald-50 text-emerald-700 border border-emerald-200";
		case "pending":
			return "bg-amber-50 text-amber-700 border border-amber-200";
		case "rejected":
			return "bg-red-50 text-red-700 border border-red-200";
		case "duplicate":
			return "bg-sky-50 text-sky-700 border border-sky-200";
		case "bot":
			return "bg-zinc-100 text-zinc-600 border border-zinc-200";
		default:
			return "bg-zinc-100 text-zinc-600 border border-zinc-200";
	}
}

export function countryBadgeClass(): string {
	return "bg-zinc-100 text-zinc-700 border border-zinc-200 font-mono";
}

/** 24h-ago unix seconds, rounded to minute — stable default for "last 24h". */
export function last24hSince(now: number = Date.now()): number {
	return Math.floor((now - 24 * 60 * 60 * 1000) / 1000);
}

export function nowSec(now: number = Date.now()): number {
	return Math.floor(now / 1000);
}

export const EXPORT_WARN_THRESHOLD = 10_000;
